import { Box } from "@material-ui/core"
import { useEffect } from "react"
import type { ReactElement } from "react"
import { Trans } from "react-i18next"
import { connect } from "react-redux"

import { SearchBar, SearchBarProvider, SearchResultEmpty } from ".."
import { AnalyticsSearchModule } from "../../../models"
import type {
  AppState,
  AppThunkDispatch,
  MapSearchResult,
  Project,
  SearchActions,
  SearchResultState
} from "../../../redux"
import {
  changeSearchValue,
  performMapSearch,
  searchClear,
  searchClearFilters,
  searchSetPreserveState
} from "../../../redux"
import { ClearFiltersButton, Header3, LoadingContainer } from "../../atoms"
import { PageContainer, SearchResultFilter, SearchResultList } from "../../molecules"

import "./search-result-viewer.styles.scss"

export interface ISearchResultViewerOwnProps {
  onMapClicked: (map: MapSearchResult) => void
  onMapPreview?: (map: MapSearchResult) => void
  onNavigateToIndex: () => void
  projectSelection?: Project[]
}

export interface ISearchResultViewerStateProps {
  searchValue: string
  results: SearchResultState
  filterSelected: boolean
  setSearchValue: (searchValue: string) => void
  searchMaps: (searchValue: string) => void
  clearState: () => void
  clearFilters: () => void
  setPreserveSearchState: () => void
}

function SearchResultViewerView(props: ISearchResultViewerOwnProps & ISearchResultViewerStateProps): ReactElement {
  const {
    searchValue,
    results,
    projectSelection,
    setSearchValue,
    searchMaps,
    onMapClicked,
    onMapPreview,
    clearState,
    clearFilters,
    filterSelected,
    onNavigateToIndex,
    setPreserveSearchState
  } = props
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    return () => {
      clearState()
    }
  }, [])

  function hasSearchValue(): boolean {
    return searchValue.length > 0
  }

  function PageHeader(): ReactElement {
    return hasSearchValue() ? (
      <Header3 className="page-header">
        <Trans
          i18nKey={"search:resultsFound"}
          values={{ searchValue }}
          components={{ bold: <strong /> }}
          defaults='Results for "<bold>{{searchValue}}</bold>"'
        />
      </Header3>
    ) : (
      <></>
    )
  }

  function isEmptyState(): boolean {
    return (!filterSelected && results.count === 0) || searchValue.length === 0
  }

  function search(searchValue: string): void {
    setSearchValue(searchValue)
    searchMaps(searchValue)
  }

  function handleOnMapClicked(map: MapSearchResult): void {
    setPreserveSearchState()
    onMapClicked(map)
  }

  return (
    <PageContainer id="search-result-page">
      {isEmptyState() ? (
        <SearchResultEmpty
          selectable={projectSelection !== undefined}
          search={search}
          searchValue={searchValue}
          onNavigateToIndex={onNavigateToIndex}
        />
      ) : (
        <Box className="results">
          {filterSelected && <ClearFiltersButton onClick={clearFilters} />}
          <SearchResultFilter />
          <SearchBarProvider disableDropdown disableNavigation autoFocus>
            <SearchBar />
          </SearchBarProvider>
          <Box className="results-list">
            <PageHeader />
            <LoadingContainer sections={["search"]}>
              <SearchResultList
                onMapClicked={handleOnMapClicked}
                onMapPreview={onMapPreview}
                projectSelection={projectSelection}
              />
            </LoadingContainer>
          </Box>
        </Box>
      )}
    </PageContainer>
  )
}

function mapStateToProps(state: AppState) {
  return {
    searchValue: state.search.searchValue,
    results: state.search.searchResults,
    filterSelected: state.search.filterSelected
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<SearchActions>) {
  return {
    setSearchValue: (searchValue: string) => dispatch(changeSearchValue(searchValue)),
    searchMaps: (searchValue: string) => dispatch(performMapSearch(searchValue, AnalyticsSearchModule.Main)),
    clearState: () => dispatch(searchClear()),
    clearFilters: () => dispatch(searchClearFilters()),
    setPreserveSearchState: () => dispatch(searchSetPreserveState())
  }
}

export const SearchResultViewer = connect(mapStateToProps, mapDispatchToProps)(SearchResultViewerView)
